import ocSmileys from '../openclassrooms';

const smileysCodes = Object.keys(ocSmileys as Record<string, string>);

function escapeRegexp(regexpString: string) {
  return regexpString.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function sortFromIndex(a: { index: number }, b: { index: number }) {
  return a.index - b.index;
}

function createImageFromSmileyCode(smileyCode: string) {
  const image = document.createElement('img');
  const smiley = (ocSmileys as any)[smileyCode];
  // import of images in next js returns an object and not a string like in the legacy app
  image.src = typeof smiley === 'string' ? smiley : smiley.src;
  image.setAttribute('title', smileyCode);
  image.setAttribute('alt', smileyCode);
  return image;
}

function getSplitDataFromSmileyCode(textNode: Text, smileyCode: string) {
  const codeLength = smileyCode.length;
  const splitsData: Array<{ index: number; smileyCode: string; codeLength: number }> = [];
  let startIndexOf = 0;
  let index: number;

  do {
    index = textNode.nodeValue!.indexOf(smileyCode, startIndexOf);
    const end = index + codeLength;
    if (index >= 0) {
      splitsData.push({ index, smileyCode, codeLength });
      startIndexOf += end;
    }
  } while (index >= 0);

  return splitsData;
}

function splitTextNode(textNode: Text) {
  let clone = textNode.cloneNode() as Text;
  const splitedNodeData: { originalNode: Text; replacedNodes: Node[] } = {
    originalNode: textNode,
    replacedNodes: [clone],
  };

  const splitsData = smileysCodes.reduce((allDatas, smileyCode) => {
    const newData = getSplitDataFromSmileyCode(clone, smileyCode);
    if (!newData.length) return allDatas;
    const smileyRegexp = new RegExp(escapeRegexp(smileyCode), 'g');
    clone.nodeValue = clone.nodeValue!.replace(smileyRegexp, '_'.repeat(smileyCode.length));
    return [...allDatas, ...newData];
  }, [] as Array<{ index: number; smileyCode: string; codeLength: number }>);

  if (!splitsData.length) return null;

  let deltaIndex = 0;
  splitsData.sort(sortFromIndex).forEach((data) => {
    const image = createImageFromSmileyCode(data.smileyCode);
    splitedNodeData.replacedNodes.push(image);

    const nextNode = (clone as any).splitText(data.index - deltaIndex) as Text;
    nextNode.nodeValue = nextNode.nodeValue!.substring(data.codeLength);
    splitedNodeData.replacedNodes.push(nextNode);

    deltaIndex += (clone.nodeValue || '').length + data.codeLength;
    clone = nextNode;
  });

  return splitedNodeData;
}

export function parse(domContainer: HTMLElement) {
  function textNodesExclusionFunction(node: Element) {
    const notParsedTags = ['code', 'pre', 'samp'];
    const nodeTagName = (node as any).tagName ? (node as any).tagName.toLowerCase() : null;
    const shouldBeParsed = notParsedTags.every((tag) => tag !== nodeTagName);
    return !(node as any).tagName || shouldBeParsed;
  }

  // Collect text nodes excluding code/pre/samp
  const walker = document.createTreeWalker(domContainer, NodeFilter.SHOW_TEXT, {
    acceptNode: (node: any) => {
      const parent = node.parentElement as Element | null;
      if (!parent) return NodeFilter.FILTER_REJECT;
      return textNodesExclusionFunction(parent)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  } as any);

  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  const splittedNodes = textNodes
    .map((textNode) => splitTextNode(textNode))
    .filter(Boolean) as Array<{ originalNode: Text; replacedNodes: Node[] }>;

  splittedNodes.forEach((splittedNode) => {
    const { parentNode } = splittedNode.originalNode;
    splittedNode.replacedNodes.forEach((node) => {
      parentNode!.insertBefore(node, splittedNode.originalNode);
    });
    parentNode!.removeChild(splittedNode.originalNode);
  });
}

export default { parse };
